import React from 'react';
import {Link} from 'react-router-dom';
import styles from './AppBar.module.css';
import LogoMark from '../marketing/components/Logo';
import Button from '../marketing/components/Button';
import AccountMenu from './AccountMenu';
import MoreMenu from './MoreMenu';
import {ChevronLeftIcon, HelpIcon, PlusIcon, DownloadIcon} from '../marketing/icons/Icons';
import {HOME_ROUTE, CREATE_PAGE_ROUTE, HOW_TO_ROUTE} from '../marketing/routes';

const PAGES_ROUTE = '/pages';

/**
 * The app's own top bar, replacing the marketing nav inside the app.
 * `minimal` is used while the create-a-page dialog is open and no page
 * exists yet (matches the reference's bare "My pages" backdrop state).
 */
const AppBar = ({
    minimal,
    pageName,
    unsaved,
    saveInProgress,
    onSave,
    onDownload,
    onPageDetails,
    authentication,
}) => (
    <header className={styles.bar}>
        <div className={styles.left}>
            {minimal ? (
                <>
                    <LogoMark width={36} height={20}/>
                    <span className={styles.plainCrumb}>My pages</span>
                </>
            ) : (
                <>
                    <Link to={HOME_ROUTE} aria-label="Ovalview home" className={styles.logoLink}>
                        <LogoMark width={36} height={20}/>
                    </Link>
                    <span className={styles.divider}/>
                    <Link to={PAGES_ROUTE} className={styles.backChevron} aria-label="Back to My pages">
                        <ChevronLeftIcon/>
                    </Link>
                    <nav aria-label="Breadcrumb" className={styles.breadcrumb}>
                        <Link to={PAGES_ROUTE} className={styles.crumbLink}>My pages</Link>
                        <span className={styles.crumbSep}>/</span>
                        <span aria-current="page" className={styles.pageName}>{pageName}</span>
                    </nav>
                    <div className={styles.mobileNameBlock}>
                        <span className={styles.pageName}>{pageName}</span>
                        {unsaved && (
                            <span className={styles.unsavedInline}>
                                <span className={styles.unsavedDot}/>Unsaved changes
                            </span>
                        )}
                    </div>
                    {unsaved && (
                        <span className={styles.unsavedPill}>
                            <span className={styles.unsavedDot}/>Unsaved changes
                        </span>
                    )}
                </>
            )}
        </div>

        {!minimal && (
            <div className={styles.right}>
                <Link to={HOW_TO_ROUTE} aria-label="Help: how pages work" className={styles.iconButton}>
                    <HelpIcon/>
                </Link>
                <Button to={CREATE_PAGE_ROUTE} variant="outline" size="nav" className={styles.newPageButton}>
                    <PlusIcon/>New page
                </Button>
                <span className={styles.divider}/>
                <button type="button" aria-label="Download page" className={styles.iconButtonOutline}
                        onClick={onDownload}>
                    <DownloadIcon/>
                </button>
                <button type="button" className={styles.saveButton} onClick={onSave} disabled={saveInProgress}>
                    {saveInProgress ? 'Saving…' : 'Save'}
                </button>
                <span className={styles.accountMenuSlot}>
                    <AccountMenu authentication={authentication}/>
                </span>
                <MoreMenu authentication={authentication} onDownload={onDownload} onPageDetails={onPageDetails}/>
            </div>
        )}
    </header>
);

export default AppBar;
